/*
 * Copyright (c) CIRROM 2025.
 */

import { IntegratedSystem } from "../api/integratedsystems";
import { DataProject } from "../api/dataprojects";
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSystemOrProject } from "../components/integrated-systems/hooks";
import {
  useSystemDetails,
  useSystemStats,
} from "../hooks/components/dashboard/useDashboardData";
import { useDashboardStatsSplitByEntity } from "../hooks/components/dashboard/useDashboardStats";
import { Helmet } from "react-helmet";
import { LoadingDisplay } from "../components/dashboard/LoadingDisplay";
import { DashboardAccountStats } from "../components/dashboard/DashboardAccountStats";

interface AccountDashboardProps {
  userSystems: IntegratedSystem[] | DataProject[];
  setSelectedSystemId: (id: number) => void;
  isDataProject?: boolean;
}

export const AccountDashboard = React.memo(
  (props: AccountDashboardProps) => {
    const navigate = useNavigate();
    const { integratedSystemId } = useParams();

    useEffect(() => {
      if (integratedSystemId) {
        if (props.isDataProject) {
          // For data projects, find the project and use its integrated_system_id
          const dataProject = (props.userSystems as DataProject[]).find(
            (p) => p.id === Number.parseInt(integratedSystemId)
          );
          if (dataProject) {
            props.setSelectedSystemId(dataProject.integrated_system_id);
          }
        } else {
          props.setSelectedSystemId(Number.parseInt(integratedSystemId));
        }
      }
    }, [integratedSystemId, props.isDataProject, props.userSystems]);

    const systemOrProject = useSystemOrProject(
      props.userSystems,
      integratedSystemId
    );

    const systemStats = useSystemStats(
      props.isDataProject ? null : systemOrProject,
      props.isDataProject && systemOrProject ? systemOrProject.id : null
    );

    const statsByEntity = useDashboardStatsSplitByEntity(
      systemStats.systemStats,
      integratedSystemId,
      props.isDataProject
    );

    const systemDetails = useSystemDetails(systemOrProject);

    useEffect(() => {
      if (
        !integratedSystemId &&
        props.userSystems &&
        props.userSystems.length > 0
      ) {
        navigate(`/dashboard/${props.userSystems[0].id}`);
      }
    }, [integratedSystemId, props.userSystems, navigate]);

    if (systemDetails.isLoading) {
      return (
        <React.Fragment>
          <Helmet title="Cirrom" />
          <LoadingDisplay />
        </React.Fragment>
      );
    }

    if (!systemOrProject) {
      return (
        <React.Fragment>
          <Helmet title="Cirrom" />
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <Helmet title="Cirrom" />
        <DashboardAccountStats
          stats={statsByEntity.account}
          isLoading={systemStats.isLoading}
        />
      </React.Fragment>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.userSystems === nextProps.userSystems &&
      prevProps.isDataProject === nextProps.isDataProject &&
      prevProps.setSelectedSystemId === nextProps.setSelectedSystemId
    );
  }
);
